import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import Styles from '@/assets/js/Styles';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { addButtonSx } from '@/components/ButtonCollection';
import { BlockIcon } from '@/components/BlockIcon';
import { BaseBlock } from '@/declarations/models/blocks/BaseBlock';
import { useStore } from '@/components/store/Store';
import { moduleClipboard, useModuleClipboard } from '../lib/useModuleClipboard';

export interface ModuleClipboardMenuProps {
  /** Whether the module can be pasted in this section */
  canPaste: (block: BaseBlock) => boolean;
  onPaste: (block: BaseBlock) => void;
}

/** "Add" button for the modules kept on the clipboard. Only modules from the selected site are shown. */
export const ModuleClipboardMenu: FC<ModuleClipboardMenuProps> = ({ canPaste, onPaste }) => {
  const { t, i18n } = useTranslation('common');
  const { state } = useStore();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const siteId = state.selectedSite?.id;
  const items = useModuleClipboard().filter((i) => !i.siteId || i.siteId === siteId);

  if (!items.length) return null;

  return (
    <>
      <Button
        color='secondary'
        variant='outlined'
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          ...addButtonSx,
          // Only superadmins have the clipboard for now
          backgroundColor: Styles.Colors.SUPERADMIN_PINK,
          '&:hover': { border: `1px solid`, backgroundColor: Styles.Colors.SUPERADMIN_PINK_HOVER },
        }}>
        <MaterialSymbol name='inventory_2' />
        {t('moduleClipboard.button', { count: items.length })}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} sx={{ maxHeight: '70vh' }}>
        <Typography variant='overline' sx={{ px: 2 }}>
          {t('moduleClipboard.title')}
        </Typography>
        {items.map((item) => {
          const pastable = canPaste(item.block);
          return (
            <MenuItem
              key={item.key}
              disabled={!pastable}
              title={pastable ? undefined : t('moduleClipboard.notAllowedHere')}
              onClick={() => {
                if (!pastable) return;
                setAnchor(null);
                onPaste(item.block);
              }}
              // Keeps the remove button clickable on modules that can't be pasted here
              sx={{ '&.Mui-disabled': { pointerEvents: 'auto' }, gap: 1, maxWidth: 480 }}>
              <ListItemIcon>
                <BlockIcon blockType={item.block.type} />
              </ListItemIcon>
              <ListItemText
                primary={`${t(`BlockType.${item.block.type}`)}${item.block.title ? `: ${item.block.title}` : ''}`}
                secondary={t('moduleClipboard.from', {
                  page: item.pageTitle || item.pageId || '?',
                  time: new Date(item.addedAt).toLocaleString(i18n.language, {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  }),
                })}
                slotProps={{ primary: { noWrap: true }, secondary: { noWrap: true } }}
              />
              <IconButton
                size='small'
                aria-label={t('moduleClipboard.remove')}
                title={t('moduleClipboard.remove')}
                onClick={(e) => {
                  e.stopPropagation();
                  moduleClipboard.remove(item.key);
                }}>
                <MaterialSymbol name='close' sx={{ fontSize: 18 }} />
              </IconButton>
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            moduleClipboard.clear();
          }}>
          <ListItemIcon>
            <MaterialSymbol name='delete_sweep' />
          </ListItemIcon>
          <ListItemText>{t('moduleClipboard.clear')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ModuleClipboardMenu;
